import React, { useCallback, useEffect, useState } from "react";
import { GridCellModes, useGridApiContext } from "@mui/x-data-grid-pro";
import IbDataGrid from "./IbDataGrid";
import useHttp from "../../common/Hooks/use-Http";
import { IB_DEALER_RC, ROLES } from "../../constants/constant";
import ToastMessageFunction from "../../common/toast-message-function";
import { getUserRole } from "../../utils/utils";
import { useSelector } from "react-redux";
import { Select, Option } from "@deere/fuel-react";
import styles from "../../css/rc.module.css";

const ReasonCodeDetailedPanel = (props) => {
  const {
    rateTypeId,
    delaerId,
    rateTypeIdAndRateTypeMap,    
    rateTypeAndRateMap,
    rateTypeAndRateTypeIdMap,
    dealerRateTypeAndRatesMapping,
    startEndDataAndFisMonthMap,
    handleEditedData,
    countryCodeMap,
    selectedMonth,
    selectedCountry,
    isParentLocked,
  } = props;

  const toastManager = ToastMessageFunction();
  const userData = useSelector((state) => state.userState.userData);
  const [dealerReasonCodesList, setDealerReasonCodesList] = useState([]);
  const [cellModesModel, setCellModesModel] = useState({});
  const [editedRates, setEditedRates] = useState([]);
  const [childRateTypeDTOList, setChildRateTypeDTOList] = useState([]);
  const { isLoading: isDealerRCListLoading, sendRequest: fetchDealerRCList, error: dealerRCListError } = useHttp();

  // Handling Errors
  const showToast = useCallback(
    (message, severity) => {
      toastManager.getToast(message, severity);
    },
    [toastManager]
  );

  const getCustomRowId = (row) => row.dealerReasonCodeTransactionId;

  const getRate = (data) => dealerRateTypeAndRatesMapping[data];

  const CustomEditComponent = (props) => {
    const apiRef = useGridApiContext();
    const [selectedValue, setSelectedValue] = useState(
      props.options.find((data) => {
        const lastIndex = data.lastIndexOf("-");
        const country = data.substring(0, lastIndex).trim();
        return country === props.row.rateType && props.row.rateType?.trim();
      }) || props.options.find((data) => data === props.row.rateType)
    );

    const handleValueChange = (event) => {
      const newValue = event.target.value;     
      setSelectedValue(newValue);
      apiRef.current.setEditCellValue({
        id: props.row.dealerReasonCodeTransactionId,
        field: "rate",
        value: dealerRateTypeAndRatesMapping[newValue],
      });
      apiRef.current.setEditCellValue({
        id: props.row.dealerReasonCodeTransactionId,
        field: "rateType",
        value: newValue,
      });
      handleEditChange(props.row, rateTypeAndRateTypeIdMap[newValue], newValue);
    };

    return (
      <div className={styles.rateTypeDropdown}>
        <Select autoFocus width={"100%"} minHeight={"100%"} value={selectedValue} onChange={handleValueChange}>
          {props?.options?.map((item) => (
            <Option key={item} value={item}>
              {item}
            </Option>
          ))}
        </Select>
      </div>
    );
  };

  const handleEditChange = (data, rateTypeId, rateType) => {
    setEditedRates((prev) => {
      const updatedRates = [...prev];
      const existingIndex = updatedRates.findIndex(
        (item) => item.dealerReasonCodeTransactionId === data.dealerReasonCodeTransactionId
      );
      const updatedData = {
        ...data,
        rateTypeId,
        rateType: rateTypeIdAndRateTypeMap[rateTypeId],
        rate: rateTypeAndRateMap[rateType],
        referenceTable: rateTypeIdAndRateTypeMap[rateTypeId].includes("Custom") ? "customratetype_master" : "ratetype",
      };

      if (existingIndex > -1) {
        updatedRates[existingIndex] = updatedData;
      } else {
        updatedRates.push(updatedData);
      }

      return updatedRates;
    });
  };

  const gettingDealerRCList = (dealerRCListData) => {
    setChildRateTypeDTOList(dealerRCListData.rateTypeDTOList);
    setDealerReasonCodesList(dealerRCListData.dealerReasonCodeTransactionDtoList);
  };

  const getDealerRCData = (dealerid) => {
    fetchDealerRCList(
      {
        method: "get",
        url: IB_DEALER_RC,
        customErrorMsg: "Failed to Fetch Reason Code List",
        params: {
          fisMonthYear: startEndDataAndFisMonthMap[selectedMonth],
          country: countryCodeMap[selectedCountry],
          dealerid,
          rateTypeId
        },
      },
      gettingDealerRCList
    );
  };
  useEffect(() => {
    getDealerRCData(delaerId);
  }, [delaerId]);

  useEffect(() => {
    const newCellModesModel = {};


    dealerReasonCodesList.forEach((row) => {
      newCellModesModel[row.dealerReasonCodeTransactionId] = {
        rateType: { mode: getUserRole(userData) === ROLES.CCM && !isParentLocked ? GridCellModes.Edit : GridCellModes.View, ignoreModifications: true },
        rate: { mode: getUserRole(userData) === ROLES.CCM && !isParentLocked ? GridCellModes.Edit : GridCellModes.View, ignoreModifications: true },
      };
    });
    setCellModesModel(newCellModesModel);
  }, [dealerReasonCodesList, userData, isParentLocked]);

  useEffect(() => {
    handleEditedData(editedRates);
  }, [editedRates, handleEditedData]);

  useEffect(() => {
    if (dealerRCListError) showToast(dealerRCListError, "error");
  }, [dealerRCListError, showToast]);

  const columns = [
    {
      field: "reasonCode",
      width: 260,
      editable: false,
      renderHeader: () => <strong>Reason Code</strong>,
    },
    {
      field: "rateType",
      width: 250,
      height: 50,
      editable: true,
      type: "singleSelect",
      valueOptions: childRateTypeDTOList.length > 0
        ? childRateTypeDTOList.map((item) => `${item.rateTypes}-${item.currency}`)
        : [],
      renderCell: (params) => {
        if (!params.row.rateType) return "";
        return `${params.row.rateType}-${params.row.currency}`;
      },
      renderEditCell: (params) => (
        <CustomEditComponent
          options={
            childRateTypeDTOList.length > 0
              ? childRateTypeDTOList.map((item) => `${item.rateTypes}-${item.currency}`)
              : []
          }
          {...params}
        />
      ),
      renderHeader: () => <strong>Rate Type</strong>,
    },
    {
      field: "rate",
      width: 200,
      editable: true,
      renderHeader: () => <strong>Rate (%)</strong>,
      valueOptions: ({ row }) => {
        if (!row.rateType) return " ";
        return getRate(`${row.rateType}-${row.currency}`);
      },
    },
  ];

  return (
    <div className={styles.rcWrapper}>
      {toastManager.toast}
      <IbDataGrid
        loading={isDealerRCListLoading}
        editMode="batch"
        getRowId={getCustomRowId}
        columns={columns}
        rows={dealerReasonCodesList}
        cellModesModel={cellModesModel}
        initialState={{
          sorting: {
            sortModel: [{ field: "reasonCode", sort: "asc" }],
          },
        }}
      />
    </div>
  );
};

export default ReasonCodeDetailedPanel;
